import { useCallback, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { HoverHelp } from '@/tableControls'
import type { OneTimeItem } from './engine/types'
import NoteMarker from './NoteMarker'
import {
  BARE_TRIGGER,
  CELL_INPUT,
  HIST_COL,
  NOTE_CELL_BG,
  OVERRIDE_CELL_BG,
  PROJ_EDGE,
  SUB_VALUE,
  VALUE,
  type PickerDriver,
} from './gridClasses'
import {
  denomZeroHelpText,
  driverInputUnit,
  formatDriverInput,
  isDenomZero,
  isIdentity,
  parseDriverInput,
} from './driverUtils'
import type { ExplainModel } from './explain/explainCell'
import { ExplainPanelContent } from './explain/ExplainPanelContent'
import { formatByUnit } from './projectionsFormat'

export function formatCompareDelta(
  unit: 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct' | 'text',
  delta: number | null,
): string {
  if (delta == null || !Number.isFinite(delta)) return '-'
  if (unit === 'pct') {
    const base = formatByUnit('pct', delta)
    const pts = base.replace('%', ' pts')
    return delta > 0 ? `+${pts}` : pts
  }
  const base = formatByUnit(unit, delta)
  return delta > 0 ? `+${base}` : base
}

type GridCellProps = {
  lineKey: string
  lineLabel: string
  monthKey: string
  t: number
  isFirstProjected: boolean
  driverType: PickerDriver | null
  driverInput: number | null
  computed: number | null
  valueUnit: 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct'
  oneTimeItems: OneTimeItem[]
  canEdit: boolean
  cellKey: string
  note?: string
  onOpenNote?: (cellKey: string, position: { x: number; y: number }, context: string) => void
  onOpenExplain?: (anchor: DOMRect | { x: number; y: number }) => void
  onCommit: (t: number, value: number | null) => void
  onTabNext: (t: number) => void
  onApplyFromColumn?: (t: number, mode?: 'keep_shape' | 'replace_flat') => void
  identityExplain?: ExplainModel | null
  hasOverride?: boolean
  payDateCount?: number | null
  cellRule?: string
}

type CommitReason = 'blur' | 'enter' | 'tab' | 'escape' | null

function valuesEqual(a: number | null, b: number | null): boolean {
  if (a === b) {
    return true
  }
  if (a == null || b == null) {
    return false
  }
  return Math.abs(a - b) < 1e-9
}

const IDENTITY_BADGE_CLASS =
  'rounded-sm border border-gray-200 bg-gray-50 px-1 text-[10px] font-medium leading-none text-gray-600'

function ApplyForwardBadge({
  disabled,
  onClick,
}: {
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={`${IDENTITY_BADGE_CLASS} disabled:cursor-not-allowed disabled:opacity-40`}
      disabled={disabled}
      aria-label="Apply this month's input to later months"
      onClick={onClick}
    >
      →
    </button>
  )
}

function IdentityBadge({
  explain,
  contextLabel,
}: {
  explain?: ExplainModel | null
  contextLabel: string
}) {
  const badge = (
    <span className={IDENTITY_BADGE_CLASS} aria-label="Input equals value">
      =
    </span>
  )
  if (!explain) {
    return badge
  }
  return (
    <span className={BARE_TRIGGER}>
      <HoverHelp aria-label={`Recipe — ${contextLabel}`} label={badge}>
        <div className="max-w-xs">
          <p className="mb-2 text-sm font-medium text-gray-900">{explain.header}</p>
          <ExplainPanelContent model={explain} />
        </div>
      </HoverHelp>
    </span>
  )
}

function OneTimeBadge({
  items,
  context,
}: {
  items: OneTimeItem[]
  context: string
}) {
  const body = (
    <div className="space-y-1">
      {items.map((item) => (
        <p key={item.id} className="text-sm text-gray-600">
          {item.notes?.trim()
            ? item.notes
            : `${formatByUnit('usd', item.amount)} (${item.mode === 'incremental' ? 'incremental' : 'already in driver'})`}
        </p>
      ))}
    </div>
  )
  return (
    <span className={BARE_TRIGGER}>
      <HoverHelp
        aria-label={`One-time item — ${context}`}
        label={
          <span className="rounded-sm border border-gray-500 px-1 text-[10px] font-medium leading-none text-gray-600">
            1&times;
          </span>
        }
      >
        {body}
      </HoverHelp>
    </span>
  )
}

function DenominatorZero({ driverType }: { driverType: PickerDriver }) {
  return (
    <HoverHelp aria-label="Value cannot be computed" label="-">
      <p className="text-sm text-gray-600">{denomZeroHelpText(driverType)}</p>
    </HoverHelp>
  )
}

export default function GridCell({
  lineKey,
  lineLabel,
  monthKey,
  t,
  isFirstProjected,
  driverType,
  driverInput,
  computed,
  valueUnit,
  oneTimeItems,
  canEdit,
  cellKey,
  note,
  onOpenNote,
  onOpenExplain,
  onCommit,
  onTabNext,
  onApplyFromColumn,
  identityExplain = null,
  hasOverride = false,
  payDateCount = null,
  cellRule = '',
}: GridCellProps) {
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const commitReasonRef = useRef<CommitReason>(null)

  const hasDriver = driverType != null
  const isScurve = driverType === 's_curve'
  const isCarryForward = driverType === 'carry_forward'
  const isIgnore = driverType === 'ignore'
  const hasEditableDriver = hasDriver && !isScurve && !isCarryForward && !isIgnore
  const showDriverInput = hasDriver && !isScurve && !isIgnore
  const driverUnit = driverType && !isCarryForward ? driverInputUnit(driverType) : 'usd'
  const denomZero = isDenomZero(driverType, driverInput, computed)
  const hasOneTime = oneTimeItems.length > 0
  const cellContext = `${lineLabel}, ${monthKey}`
  const showDollarPrefix = showDriverInput && !isCarryForward && driverUnit === 'usd'
  const showPctSuffix = hasEditableDriver && driverUnit === 'pct'
  const showApplyForward =
    t >= 1 &&
    t < 36 &&
    hasEditableDriver &&
    canEdit &&
    onApplyFromColumn != null
  const applyForwardDisabled = driverInput == null

  const displayValue =
    focused && hasEditableDriver
      ? draft
      : isCarryForward
        ? formatByUnit(valueUnit, computed)
        : hasEditableDriver
          ? formatDriverInput(driverType!, driverInput)
          : ''

  const identity =
    hasEditableDriver &&
    isIdentity(driverType!, driverUnit, valueUnit, driverInput, computed, {
      hasOneTime,
      denomZero,
    })

  const showSubValue = isScurve || (hasEditableDriver && !identity)

  const subValueContent = (() => {
    if (isIgnore || !hasDriver || !driverType) return <span>-</span>
    if (isScurve) return <span>{formatByUnit(valueUnit, computed)}</span>
    if (identity) {
      return <IdentityBadge explain={identityExplain} contextLabel={cellContext} />
    }
    if (!showSubValue) return <span />
    if (denomZero) return <DenominatorZero driverType={driverType} />
    return (
      <span className="inline-flex items-center gap-1">
        <span>{formatByUnit(valueUnit, computed)}</span>
        {driverType === 'dollar_per_pay_period' && payDateCount === 3 ? (
          <span className="rounded-sm border border-gray-500 px-1 text-[10px] font-medium leading-none text-gray-600">
            &times;3
          </span>
        ) : null}
      </span>
    )
  })()

  const commitIfChanged = useCallback(
    (reason: CommitReason) => {
      if (!driverType || isScurve || isCarryForward || isIgnore || reason === 'escape') {
        return
      }
      const parsed = parseDriverInput(driverType, draft)
      if (!valuesEqual(parsed, driverInput)) {
        onCommit(t, parsed)
      }
      commitReasonRef.current = reason
    },
    [driverInput, driverType, draft, isCarryForward, isIgnore, isScurve, onCommit, t],
  )

  const handleFocus = useCallback(() => {
    if (!driverType || isScurve || isCarryForward || isIgnore) return
    commitReasonRef.current = null
    setFocused(true)
    setDraft(formatDriverInput(driverType, driverInput))
  }, [driverType, driverInput, isCarryForward, isScurve])

  const handleBlur = useCallback(() => {
    if (!driverType || isScurve || isCarryForward || isIgnore) return
    setFocused(false)
    const reason = commitReasonRef.current
    commitReasonRef.current = null
    if (reason === 'enter' || reason === 'tab' || reason === 'escape') {
      return
    }
    commitIfChanged('blur')
  }, [commitIfChanged, driverType, isCarryForward, isScurve])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!driverType || isScurve || isCarryForward || isIgnore) return
      if (e.key === 'Enter') {
        e.preventDefault()
        commitIfChanged('enter')
        inputRef.current?.blur()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        commitReasonRef.current = 'escape'
        setDraft(formatDriverInput(driverType, driverInput))
        inputRef.current?.blur()
      } else if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault()
        commitIfChanged('tab')
        onTabNext(t)
      }
    },
    [commitIfChanged, driverInput, driverType, isCarryForward, isScurve, onTabNext, t],
  )

  const handleContextMenu = useCallback(
    (event: MouseEvent<HTMLTableCellElement>) => {
      if (!canEdit || !onOpenNote) return
      event.preventDefault()
      onOpenNote(cellKey, { x: event.clientX, y: event.clientY }, cellContext)
    },
    [canEdit, cellContext, cellKey, onOpenNote],
  )

  const handleCellMouseDown = useCallback(
    (event: MouseEvent<HTMLTableCellElement>) => {
      if (!onOpenExplain || !event.altKey) return
      event.preventDefault()
      event.stopPropagation()
      onOpenExplain({ x: event.clientX, y: event.clientY })
    },
    [onOpenExplain],
  )

  return (
    <td
      className={`group relative ${note ? NOTE_CELL_BG : ''} ${hasOverride ? OVERRIDE_CELL_BG : ''} ${cellRule} ${isFirstProjected ? PROJ_EDGE : ''}`}
      onContextMenu={handleContextMenu}
      onMouseDown={handleCellMouseDown}
    >
      {hasOverride ? <span className="proj-override-corner" data-testid="override-corner" /> : null}
      {note ? <NoteMarker note={note} context={cellContext} /> : null}
      {showDriverInput ? (
        <div className="relative min-w-0">
            {showDollarPrefix ? (
              <span className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                $
              </span>
            ) : null}
            <input
              ref={inputRef}
              data-proj-input={`${lineKey}-${t}`}
              className={`${CELL_INPUT}${showDollarPrefix ? ' !pl-5' : ''}${showPctSuffix ? ' !pr-5' : ''}`}
              placeholder="-"
              value={displayValue}
              disabled={!canEdit || isCarryForward}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {showPctSuffix ? (
              <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                %
              </span>
            ) : null}
        </div>
      ) : hasDriver && isScurve ? (
        <div className="h-7" />
      ) : (
        <div className="flex h-7 items-center justify-end text-sm text-gray-500">-</div>
      )}
      <div className={`mt-0.5 flex h-4 items-center justify-between gap-1 ${SUB_VALUE}`}>
        <div className="min-w-0 shrink-0">
          {hasOneTime ? <OneTimeBadge items={oneTimeItems} context={cellContext} /> : <span />}
        </div>
        <div className="min-w-0 flex-1 text-right">
          {hasDriver ? (
            <span className="inline-flex items-center justify-end gap-1">
              {subValueContent}
              {showApplyForward ? (
                <ApplyForwardBadge
                  disabled={applyForwardDisabled}
                  onClick={() => onApplyFromColumn?.(t + 1)}
                />
              ) : null}
            </span>
          ) : (
            <span>-</span>
          )}
        </div>
      </div>
    </td>
  )
}

export function ReadOnlyValueCell({
  value,
  unit,
  className = '',
  isFirstProjected = false,
}: {
  value: number | string | null | undefined
  unit: 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct' | 'text'
  className?: string
  isFirstProjected?: boolean
}) {
  const text = unit === 'text' ? formatByUnit('text', value) : formatByUnit(unit, value as number | null)
  return (
    <td className={`${className} ${isFirstProjected ? PROJ_EDGE : ''}`}>
      <div className={`flex justify-end ${unit === 'text' ? 'text-sm text-gray-900' : VALUE}`}>
        <span className="truncate">{text}</span>
      </div>
    </td>
  )
}

const EMPTY_SUBTITLE_DRIVERS = new Set<PickerDriver>([
  'count_value',
  'dollar_value',
  'carry_forward',
  'ignore',
  's_curve',
])

export function HistoryValueCell({
  value,
  unit,
  isInputRow = false,
  driverType = null,
  impliedDriver = null,
  cellKey,
  note,
  context,
  canEdit = false,
  onOpenNote,
  cellRule = '',
  compare,
}: {
  value: number | string | null | undefined
  unit: 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct' | 'text'
  isInputRow?: boolean
  driverType?: PickerDriver | null
  impliedDriver?: number | null
  cellKey?: string
  note?: string
  context?: string
  canEdit?: boolean
  onOpenNote?: (cellKey: string, position: { x: number; y: number }, context: string) => void
  cellRule?: string
  compare?: {
    delta: number | null
    deltaPct: number | null
    projected: number | null
    hover: string
  }
}) {
  const handleContextMenu = useCallback(
    (event: MouseEvent<HTMLTableCellElement>) => {
      if (!canEdit || !onOpenNote || !cellKey || !context) return
      event.preventDefault()
      onOpenNote(cellKey, { x: event.clientX, y: event.clientY }, context)
    },
    [canEdit, cellKey, context, onOpenNote],
  )

  const text = formatByUnit(unit, value)
  if (isInputRow) {
    const showImpliedSubtitle =
      compare == null && driverType != null && !EMPTY_SUBTITLE_DRIVERS.has(driverType)
    const subtitle = compare
      ? formatCompareDelta(unit, compare.delta)
      : showImpliedSubtitle
        ? formatByUnit(driverInputUnit(driverType), impliedDriver)
        : null
    const valueBlock = (
      <>
        <div className="flex h-7 items-center justify-end text-right text-sm tabular-nums">
          {text}
        </div>
        <div className={`mt-0.5 flex h-4 items-center justify-end ${SUB_VALUE}`}>
          {compare || showImpliedSubtitle ? subtitle : <span />}
        </div>
      </>
    )
    return (
      <td className={`relative ${note ? NOTE_CELL_BG : ''} ${HIST_COL} ${cellRule}`} onContextMenu={handleContextMenu}>
        {note && context ? <NoteMarker note={note} context={context} /> : null}
        {compare ? (
          <span className={BARE_TRIGGER}>
            <HoverHelp aria-label={compare.hover} label={valueBlock}>
              {compare.hover}
            </HoverHelp>
          </span>
        ) : (
          valueBlock
        )}
      </td>
    )
  }
  return (
    <td className={`${HIST_COL} text-right text-sm ${unit === 'text' ? 'text-gray-900' : 'tabular-nums text-gray-600'}`}>
      <div className="flex justify-end">
        <span className="truncate">{text}</span>
      </div>
    </td>
  )
}
